// src/pages/GroupYaeChikaiPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Stack,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import {
  getDoc,
  doc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../api/firebase";
import { useTheme, useMediaQuery } from "@mui/material";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";


// ── 設定値（必要に応じて調整）
const PAGE_SIZE = 120; // 質問一覧のページング（解放済みから12件ずつ）
const AVATAR_SIZE = 40;

export default function GroupYaeChikaiPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // 認証 & 自分のusers
  const [me, setMe] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // グループ
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [groupLoading, setGroupLoading] = useState(true);

  // 質問（yaeNoInori）
  const [questions, setQuestions] = useState([]);
  const [qLoading, setQLoading] = useState(true);
  const [qError, setQError] = useState("");
  const [qLast, setQLast] = useState(null);
  const [qHasMore, setQHasMore] = useState(false);

  // 回答キャッシュ: { [qId]: { loading:boolean, answers: Array<{uid,name,avatar,text,updatedAt?}> } }
  const [answersByQ, setAnswersByQ] = useState({});

  // スライドビュー（拡大表示）
  const [slideOpen, setSlideOpen] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0); // questions の index

  const [showMineOnly, setShowMineOnly] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' | 'desc'

  // ─────────────────────────────────────────
  // 認証
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setAuthLoading(false);
        return;
      }
      try {
        const meSnap = await getDoc(doc(db, "users", u.uid));
        if (meSnap.exists()) setMe({ uid: u.uid, ...meSnap.data() });
      } finally {
        setAuthLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // ─────────────────────────────────────────
  // グループ取得 & アクセス制御
  useEffect(() => {
    const run = async () => {
      if (!id || !me) return;
      setGroupLoading(true);
      try {
        if (
          !me.isAdmin &&
          me.groups2025SecondHalf &&
          me.groups2025SecondHalf !== id
        ) {
          navigate(`/groups2025hl/${me.groups2025SecondHalf}`, {
            replace: true,
          });
          return;
        }
        const gRef = doc(db, "groups2025halfSecond", id);
        const gSnap = await getDoc(gRef);
        if (!gSnap.exists()) {
          setGroup(null);
          setMembers([]);
          return;
        }
        const g = { id: gSnap.id, ...gSnap.data() };
        setGroup(g);

        const uids = (g.members ?? [])
          .map((m) => m?.uid || m?.userId)
          .filter(Boolean);

        if (uids.length === 0) {
          setMembers([]);
          return;
        }

        // users を順番維持で取得
        const usersCol = collection(db, "users");
        // 10 件ずつ IN 取得
        const chunked = [];
        for (let i = 0; i < uids.length; i += 10)
          chunked.push(uids.slice(i, i + 10));

        const all = [];
        for (const ch of chunked) {
          const qUsers = query(usersCol, where("__name__", "in", ch));
          const snap = await getDocs(qUsers);
          snap.forEach((d) => all.push({ uid: d.id, ...d.data() }));
        }
        const order = new Map(uids.map((u, i) => [u, i]));
        all.sort((a, b) => (order.get(a.uid) ?? 0) - (order.get(b.uid) ?? 0));
        setMembers(all);
      } finally {
        setGroupLoading(false);
      }
    };
    run();
  }, [id, me, navigate]);

  // ─────────────────────────────────────────
  // 質問の初回ロード（解放済みだけ）
  const loadQuestions = useCallback(
    async (more = false) => {
      try {
        setQLoading(true);
        setQError("");

        const now = Timestamp.now();
        const base = collection(db, "yaeNoInori");
        const qBase = query(
          base,
          where("openDate", "<=", now),
          orderBy("openDate", "asc"),
          ...(more && qLast ? [startAfter(qLast)] : []),
          limit(PAGE_SIZE)
        );
        const snap = await getDocs(qBase);

        const batch = snap.docs.map((d) => ({
          id: d.id, // ← これが qId
          ...d.data(),
        }));

        if (more) {
          setQuestions((prev) => [...prev, ...batch]);
        } else {
          setQuestions(batch);
        }

        setQLast(snap.docs[snap.docs.length - 1] || null);
        setQHasMore(snap.size === PAGE_SIZE);
      } catch (e) {
        console.error(e);
        setQError("質問の取得に失敗しました");
      } finally {
        setQLoading(false);
      }
    },
    [qLast]
  );

  useEffect(() => {
    if (group) loadQuestions(false);
  }, [group, loadQuestions]);

  // ─────────────────────────────────────────
  // 質問を展開したときに、その質問の全メンバー回答を遅延取得
  const fetchAnswersForQuestion = useCallback(
    async (qId) => {
      if (!members.length) return;
      setAnswersByQ((prev) => ({
        ...prev,
        [qId]: { ...(prev[qId] || {}), loading: true },
      }));

      try {
        const perUserPromises = members.map(async (m) => {
          // users/{uid}/yaeChikaiAnswers/{qId}
          const ansRef = doc(db, `users/${m.uid}/yaeChikaiAnswers/${qId}`);
          const ansSnap = await getDoc(ansRef);
          if (!ansSnap.exists()) return null;
          const data = ansSnap.data();
          return {
            uid: m.uid,
            name: m.name || "",
            avatar: m.avatar || "",
            text: data.text || data.answer || "", // フィールド名に合わせて調整
            updatedAt: data.updatedAt,
            visible: typeof data.visible === "boolean" ? data.visible : false, // 既定: 非公開
          };
        });

        const results = await Promise.all(perUserPromises);
        const answers = results.filter(Boolean);

        setAnswersByQ((prev) => ({
          ...prev,
          [qId]: { loading: false, answers },
        }));
      } catch (e) {
        console.error(e);
        setAnswersByQ((prev) => ({
          ...prev,
          [qId]: { loading: false, answers: [], error: "取得に失敗しました" },
        }));
      }
    },
    [members]
  );

  // スライドビュー開く
  const openSlide = useCallback(
    (index) => {
      const qId = questions[index]?.id;
      if (qId && !answersByQ[qId]) {
        // 未取得なら先に取得
        fetchAnswersForQuestion(qId);
      }
      setSlideIndex(index);
      setSlideOpen(true);
    },
    [questions, answersByQ, fetchAnswersForQuestion]
  );

  const headerText = useMemo(() => {
    if (!group) return "グループで行う八重の誓い";
    return group.name || "グループで行う八重の誓い";
    // 必要なら副題や説明追加
  }, [group]);

  // 並べ替え済みの questions
  const sortedQuestions = useMemo(() => {
    const big = 1e9;
    const getNo = (q) => {
      if (typeof q.no === "number") return q.no;
      const n = Number(q.no);
      return Number.isFinite(n) ? n : sortOrder === "asc" ? big : -big; // no無は末尾へ
    };
    return [...questions].sort((a, b) =>
      sortOrder === "asc" ? getNo(a) - getNo(b) : getNo(b) - getNo(a)
    );
  }, [questions, sortOrder]);

  const loading = authLoading || groupLoading;

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "grid", placeItems: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>読み込み中…</Typography>
      </Box>
    );
  }

  if (!me) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">ログインが必要です。</Alert>
      </Box>
    );
  }

  if (!group) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">グループが見つかりませんでした。</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      // p: { xs: 2, md: 3 },
     maxWidth: 1000, mx: "auto" }}>
      {/* <Typography variant="h5" fontWeight="bold" gutterBottom>
        {headerText}
      </Typography>
      {group.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {group.description}
        </Typography>
      )} */}

      {/* ── メンバー行（横スクロール、誰が対象かを常に見せる） */}
      {/* <Paper
        variant="outlined"
        sx={{ p: 1.5, mb: 2, borderRadius: 2, overflowX: "auto" }}
      >
        <Stack direction="row" spacing={2}>
          {members.map((m) => (
            <Stack key={m.uid} alignItems="center" minWidth={72} spacing={0.5}>
              <Avatar
                src={m.avatar || ""}
                sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
              />
              <Typography
                variant="caption"
                sx={{ maxWidth: 72 }}
                noWrap
                title={m.name || ""}
              >
                {m.name || "匿名"}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper> */}

      {/* ── 解放済み質問：アコーディオン一覧 */}
      <Paper
        variant="outlined"
        sx={{ p: { xs: 1, md: 2 }, borderRadius: 2, overflow: "hidden" }}
      >
        {/* ヘッダー行 */}
        {/* <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, gap: 1 }}> */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1.5,
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {/* <Typography variant="subtitle2" sx={{ mr: "auto" }}>
            解放済みの質問（{questions.length}）
          </Typography> */}

          {/* 並び替えトグル */}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={sortOrder}
            onChange={(_, v) => v && setSortOrder(v)}
            sx={{ mr: 0.5 }}
          >
            <ToggleButton value="asc" aria-label="昇順">
              <ArrowUpwardIcon fontSize="inherit" />
              <Box component="span" sx={{ ml: 0.5 }}>
                昇順
              </Box>
            </ToggleButton>
            <ToggleButton value="desc" aria-label="降順">
              <ArrowDownwardIcon fontSize="inherit" />
              <Box component="span" sx={{ ml: 0.5 }}>
                降順
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>

          {/* 自分のだけ？全員分？ボタン */}
          <Button
            size="small"
            variant={showMineOnly ? "contained" : "text"}
            onClick={() => setShowMineOnly((v) => !v)}
            sx={{ flexShrink: 0 }}
          >
            {showMineOnly ? "全員の回答" : "自分の回答だけ"}
          </Button>
        </Box>
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            color: "text.secondary",
          }}
        >
          <Typography variant="caption" sx={{ lineHeight: 1.6, opacity: 0.9 }}>
            最新の八重の誓いを表示するには、上部「再読込」を押してください。
          </Typography>
        </Box>

        {/* アコーディオン一覧 */}
        {sortedQuestions.map((q, idx) => {
          const qId = q.id;
          const cache = answersByQ[qId];

          return (
            <Accordion
              key={qId}
              disableGutters
              sx={{
                overflow: "hidden",
                borderRadius: 1.5,
                mb: { xs: 1.25, md: 1.75 }, // ← アコーディオン間の余白を広めに
                "&:before": { display: "none" },
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              }}
              onChange={(_, expanded) => {
                if (expanded && !cache) fetchAnswersForQuestion(qId);
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  py: 1, // ← 高さは可変、余白は最小限
                  "& .MuiAccordionSummary-content": {
                    m: 0,
                    minWidth: 0,
                    overflow: "hidden",
                    alignItems: "flex-start", // ← 複数行に対応
                    gap: 1, // ← Q と本文の間を詰める（8px）
                  },
                  "& .MuiAccordionSummary-content.Mui-expanded": { m: 0 },
                }}
              >
                {/* Qバッジ（小さめにして詰める） */}
                <Box
                  sx={{
                    flexShrink: 0,
                    background: "rgba(25,118,210,0.10)",
                    color: "primary.main",
                    border: "1px solid",
                    borderColor: "primary.light",
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  Q{q.no ?? "?"}
                </Box>

                {/* 本文：challengeは2行、gainは全文（可変・折返し） */}
                <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2, // ← challengeは2行まで
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: 1.35,
                      letterSpacing: 0.1,
                      fontWeight: 600,
                    }}
                    title={q.challenge || ""}
                  >
                    {q.challenge || "（質問文未設定）"}
                  </Typography>

                  {!!q.gain && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mt: 0.25,
                        whiteSpace: "pre-wrap", // ← 改行保持で全文表示
                        wordBreak: "break-word",
                        lineHeight: 1.4,
                        opacity: 0.95,
                      }}
                    >
                      {q.gain}
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: { xs: 1.25, md: 2 } }}>
                {!cache || cache.loading ? (
                  <Box sx={{ py: 2, textAlign: "center" }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : cache.error ? (
                  <Alert severity="error">{cache.error}</Alert>
                ) : (
                  <>
                    {/* ── 自分の回答の公開スイッチ（自分が回答している時のみ） ── */}
                    {(() => {
                      const myAns = cache.answers?.find(
                        (a) => a.uid === me.uid
                      );
                      if (!myAns) return null;
                      return (
                        <Box
                          sx={{
                            p: 1,
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: "rgba(0,0,0,0.03)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            自分の回答の公開設定
                          </Typography>
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                checked={!!myAns.visible}
                                onChange={async (e) => {
                                  const next = e.target.checked;
                                  // Firestore を更新
                                  await updateDoc(
                                    doc(
                                      db,
                                      `users/${me.uid}/yaeChikaiAnswers/${qId}`
                                    ),
                                    {
                                      visible: next,
                                      // ついでに更新時刻も記録したい場合
                                      updatedAt: serverTimestamp(),
                                    }
                                  );
                                  // ローカルキャッシュを即時反映
                                  setAnswersByQ((prev) => {
                                    const cur = prev[qId];
                                    if (!cur) return prev;
                                    return {
                                      ...prev,
                                      [qId]: {
                                        ...cur,
                                        answers: (cur.answers || []).map((a) =>
                                          a.uid === me.uid
                                            ? { ...a, visible: next }
                                            : a
                                        ),
                                      },
                                    };
                                  });
                                }}
                              />
                            }
                            label={
                              myAns.visible
                                ? "公開中（グループ全員に表示）"
                                : "非公開（自分だけ見えます）"
                            }
                          />
                        </Box>
                      );
                    })()}

                    {/* ── 回答一覧 ── */}
                    {(() => {
                      const list = showMineOnly
                        ? cache.answers.filter((a) => a.uid === me.uid)
                        : cache.answers;

                      if (!list?.length) {
                        return (
                          <Typography variant="body2" color="text.secondary">
                            回答はまだありません。
                          </Typography>
                        );
                      }

                      return (
                        <Stack divider={<Divider flexItem />} spacing={1.25}>
                          {list.map((ans) => {
                            const isMe = ans.uid === me.uid;
                            const canShow = isMe || ans.visible === true; // 自分は常に表示、他人はvisibleのみ表示
                            return (
                              <Box
                                key={`${qId}-${ans.uid}`}
                                sx={{ display: "flex", gap: 1.25 }}
                              >
                                <Avatar
                                  src={ans.avatar || ""}
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    mt: 0.3,
                                    flexShrink: 0,
                                  }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="subtitle2"
                                    noWrap
                                    title={ans.name}
                                  >
                                    {ans.name || "匿名"}
                                  </Typography>

                                  {canShow ? (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {ans.text || "（未回答）"}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      非公開です
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Stack>
                      );
                    })()}
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Paper>

      {/* ── スライドビュー（1問に全員の回答を大きく表示） */}
      <Dialog
        open={slideOpen}
        onClose={() => setSlideOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {(() => {
            const q = questions[slideIndex];
            if (!q) return "拡大表示";
            return `Q${q.no ?? ""}  ${q.title || q.stepContent || ""}`;
          })()}
          <IconButton
            onClick={() => setSlideOpen(false)}
            sx={{ position: "absolute", right: 12, top: 10 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1 }}>
          {(() => {
            const q = questions[slideIndex];
            if (!q) return null;
            const cache = answersByQ[q.id];
            if (!cache) {
              // 念のため
              fetchAnswersForQuestion(q.id);
              return (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <CircularProgress size={22} />
                </Box>
              );
            }
            if (cache.loading) {
              return (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <CircularProgress size={22} />
                </Box>
              );
            }
            if (cache.error)
              return <Alert severity="error">{cache.error}</Alert>;
            if (!cache.answers?.length)
              return (
                <Typography color="text.secondary">
                  回答はまだありません。
                </Typography>
              );

            return (
              <Stack spacing={2}>
                {cache.answers.map((ans) => (
                  <Paper
                    key={`${q.id}-${ans.uid}`}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2 }}
                  >
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Avatar
                        src={ans.avatar || ""}
                        sx={{ width: 44, height: 44, mt: 0.3 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          noWrap
                          title={ans.name}
                        >
                          {ans.name || "匿名"}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            mt: 0.5,
                          }}
                        >
                          {ans.text || "（未回答）"}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button
            startIcon={<ArrowBackIosNewIcon />}
            disabled={slideIndex <= 0}
            onClick={() => {
              const next = Math.max(0, slideIndex - 1);
              setSlideIndex(next);
              const qId = questions[next]?.id;
              if (qId && !answersByQ[qId]) fetchAnswersForQuestion(qId);
            }}
          >
            前の質問
          </Button>
          <Button
            endIcon={<ArrowForwardIosIcon />}
            disabled={slideIndex >= questions.length - 1}
            onClick={() => {
              const next = Math.min(questions.length - 1, slideIndex + 1);
              setSlideIndex(next);
              const qId = questions[next]?.id;
              if (qId && !answersByQ[qId]) fetchAnswersForQuestion(qId);
            }}
          >
            次の質問
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
